import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { AttributesService } from '../../../core/services/attributes.service';
import * as AttributesActions from './attributes.actions';
import {
  selectAttributesLastQuery,
  selectAttributesMeta,
} from './attributes.selectors';
import {
  DEFAULT_ATTRIBUTES_CURRENT_PAGE,
  DEFAULT_ATTRIBUTES_PER_PAGE,
} from '../models/attribute.model';

@Injectable()
export class AttributesEffects {
  private actions$ = inject(Actions);
  private attributesService = inject(AttributesService);
  private store = inject(Store);

  loadAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttributesActions.loadAttributes),
      switchMap((action) =>
        this.attributesService
          .getPaginatedAttributes({
            page: action.page ?? DEFAULT_ATTRIBUTES_CURRENT_PAGE,
            per_page: action.perPage ?? DEFAULT_ATTRIBUTES_PER_PAGE,
            search: action.search ?? '',
            sort: action.sort ?? 'created_at',
            sort_direction: action.sortDirection ?? 'desc',
            filters: action.filters,
            filter_link: action.filterLink,
          })
          .pipe(
            map((response) =>
              AttributesActions.loadAttributesSuccess({
                attributes: response.data,
                meta: response.meta,
              }),
            ),
            catchError((error) =>
              of(
                AttributesActions.loadAttributesFailure({
                  error: error?.message ?? 'Failed to load attributes',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  submitAttribute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttributesActions.submitAttribute),
      switchMap((action) => {
        const { payload, isEditMode } = action;
        const request$ =
          isEditMode && payload.uuid
            ? this.attributesService.updateAttribute(String(payload.uuid), payload)
            : this.attributesService.createAttribute(payload);

        return request$.pipe(
          map(() => AttributesActions.submitAttributeSuccess()),
          catchError((error) =>
            of(
              AttributesActions.submitAttributeFailure({
                error: error?.message ?? 'Failed to save attribute',
              }),
            ),
          ),
        );
      }),
    ),
  );

  deleteAttribute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttributesActions.deleteAttribute),
      switchMap((action) =>
        this.attributesService.deleteAttribute(action.attributeId).pipe(
          map(() => AttributesActions.deleteAttributeSuccess()),
          catchError((error) =>
            of(
              AttributesActions.deleteAttributeFailure({
                error: error?.message ?? 'Failed to delete attribute',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  exportAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttributesActions.exportAttributes),
      switchMap((action) =>
        this.attributesService.exportAttributes(action.payload).pipe(
          map((response) =>
            AttributesActions.exportAttributesSuccess({
              jobId: response.id ?? response.data?.id,
            }),
          ),
          catchError((error) =>
            of(
              AttributesActions.exportAttributesFailure({
                error: error?.message ?? 'Failed to export attributes',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadGs1AiDefinitions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttributesActions.loadGs1AiDefinitions),
      switchMap(() =>
        this.attributesService.getGs1AiDefinitions().pipe(
          map((definitions) =>
            AttributesActions.loadGs1AiDefinitionsSuccess({ definitions }),
          ),
          catchError((error) =>
            of(
              AttributesActions.loadGs1AiDefinitionsFailure({
                error: error?.message ?? 'Failed to load GS1 definitions',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  createGs1Attribute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttributesActions.createGs1Attribute),
      switchMap((action) =>
        this.attributesService.createGs1Attribute(action.payload).pipe(
          map(() => AttributesActions.createGs1AttributeSuccess()),
          catchError((error) =>
            of(
              AttributesActions.createGs1AttributeFailure({
                error: error?.message ?? 'Failed to create GS1 attribute',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadAttributeOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttributesActions.loadAttributeOptions),
      switchMap(({ attributeId }) =>
        this.attributesService.getAttributeOptions(attributeId).pipe(
          map((options) =>
            AttributesActions.loadAttributeOptionsSuccess({
              attributeId,
              options,
            }),
          ),
          catchError((error) =>
            of(
              AttributesActions.loadAttributeOptionsFailure({
                error: error?.message ?? 'Failed to load attribute values',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AttributesActions.submitAttributeSuccess,
        AttributesActions.deleteAttributeSuccess,
        AttributesActions.createGs1AttributeSuccess,
      ),
      // Read the latest store state lazily, per emitted action, to avoid the
      // race condition where `withLatestFrom` subscribes to the selectors
      // eagerly (equivalent to @ngrx/operators `concatLatestFrom`).
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.select(selectAttributesMeta),
            this.store.select(selectAttributesLastQuery),
          ),
        ),
      ),
      map(([, meta, lastQuery]) =>
        AttributesActions.loadAttributes({
          page: meta?.currentPage ?? DEFAULT_ATTRIBUTES_CURRENT_PAGE,
          perPage: meta?.perPage ?? DEFAULT_ATTRIBUTES_PER_PAGE,
          search: lastQuery.search,
          filters: lastQuery.filters,
          filterLink: lastQuery.filterLink,
          sort: lastQuery.sort,
          sortDirection: lastQuery.sortDirection,
        }),
      ),
    ),
  );
}
